import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { FaTrashRestore } from "react-icons/fa";

import { ProfileContext } from "../contexts/profileContext";
import { PageWrapper } from "../components/admin";
import { Button, Input, InputSelect } from "../components/form";
import { BaseModal } from "../components/popups";
import { FullScreenLoader, ImageUploader } from "../components/common";
import { uploadFileIfNeeded } from "../utils";

import {
  getVideosPages,
  createVideosPage,
  updateVideosPage,
  deleteVideosPage,
  getVideosTips,
  createVideosTip,
  updateVideosTip,
  deleteVideosTip,
  getVideosExamples,
  createVideosExample,
  updateVideosExample,
  deleteVideosExample,
  restoreVideosExample,
  restoreVideosTip,
  restoreVideosPage
} from "../service";

const TABS = [
  { id: "examples", label: "Example Videos" },
  { id: "tips", label: "Video Tips" },
  { id: "pages", label: "Video Pages" }
];

const LANGUAGES = ["en", "de", "fr", "it"];

// Default structures for creating new items
const defaultExample = {
  referenceTitle: "",
  title: { en: "", de: "", fr: "", it: "" },
  description: { en: "", de: "", fr: "", it: "" },
  thumbnail: { en: "", de: "", fr: "", it: "" },
  video: { en: "", de: "", fr: "", it: "" }
};

const defaultTip = {
  referenceTitle: "",
  title: { en: "", de: "", fr: "", it: "" },
  explainerSection: {
    title: { en: "", de: "", fr: "", it: "" },
    description: { en: "", de: "", fr: "", it: "" }
  },
  tips: [],
  supportInformation: {
    title: { en: "", de: "", fr: "", it: "" },
    description: { en: "", de: "", fr: "", it: "" }
  },
  contact: {
    title: { en: "", de: "", fr: "", it: "" },
    name: "",
    email: "",
    phone: "",
    whatsapp: ""
  }
};

const defaultPage = {
  referenceTitle: "",
  index: 1,
  profilePageTitle: { en: "", de: "", fr: "", it: "" },
  profilePageDescription: { en: "", de: "", fr: "", it: "" },
  title: { en: "", de: "", fr: "", it: "" },
  description: { en: "", de: "", fr: "", it: "" },
  publicPageTitle: { en: "", de: "", fr: "", it: "" },
  thumbnail: { en: "", de: "", fr: "", it: "" },
  tipsTitle: { en: "", de: "", fr: "", it: "" },
  tipsDescription: { en: "", de: "", fr: "", it: "" },
  tipButtonText: { en: "", de: "", fr: "", it: "" },
  strengthOrRoleSelection: {
    selectionType: undefined,
    description: { en: "", de: "", fr: "", it: "" }
  }
};

const defaultTipItem = {
  id: "",
  title: { en: "", de: "", fr: "", it: "" },
  description: { en: "", de: "", fr: "", it: "" },
  icon: ""
};

// Reusable Multilingual Input Component
const MultilingualInput = ({
  label,
  value = {},
  onChange,
  placeholder = "",
  languages = LANGUAGES
}) => (
  <div className="mb-4">
    <p className="font-semibold text-sm mb-2">{label}</p>
    <div
      className="grid grid-cols-4 gap-2"
      style={{
        gridTemplateColumns: `repeat(${languages.length < 5 ? languages.length : 4}, 1fr)`
      }}>
      {languages.map((lang) => (
        <Input
          key={lang}
          className="w-full"
          inputClassName="rounded-md border border-gray-300 bg-white"
          labelClassName="text-xs font-normal text-gray-600"
          label={lang.toUpperCase()}
          placeholder={placeholder}
          value={value?.[lang] || ""}
          onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        />
      ))}
    </div>
  </div>
);

// Reusable Multilingual Image Uploader
const MultilingualImageUploader = ({
  label,
  value = {},
  onChange,
  uploadProgress = {},
  fieldId,
  languages = LANGUAGES
}) => (
  <div className="mb-4">
    <p className="font-semibold text-sm mb-2">{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {languages.map((lang) => (
        <div key={lang}>
          <ImageUploader
            key={lang}
            label={lang.toUpperCase()}
            image={value?.[lang] || ""}
            setImage={(image) => onChange({ ...value, [lang]: image })}
            uploadProgress={uploadProgress?.[`${fieldId}-${lang}`] || 0}
          />
          {value?.[lang]?.type && !uploadProgress?.[`${fieldId}-${lang}`] && (
            <p className="text-xs text-orange-500 mt-1">Pending upload</p>
          )}
        </div>
      ))}
    </div>
  </div>
);

// Single Video Preview Component (to memoize object URL)
const VideoPreview = React.memo(({ videoValue, onRemove }) => {
  const previewUrl = React.useMemo(() => {
    if (!videoValue) return null;
    if (videoValue?.type) return URL.createObjectURL(videoValue);
    return process.env.REACT_APP_AWS_URL + videoValue;
  }, [videoValue]);

  if (!previewUrl) return null;

  return (
    <div className="mb-2">
      <video src={previewUrl} className="w-full h-auto rounded" controls={true} />
      <button onClick={onRemove} className="text-xs text-red-500 mt-1">
        Remove
      </button>
    </div>
  );
});

// Reusable Multilingual Video Uploader
const MultilingualVideoUploader = ({
  label,
  value = {},
  onChange,
  uploadProgress = {},
  fieldId,
  languages = LANGUAGES
}) => {
  return (
    <div className="mb-4">
      <p className="font-semibold text-sm mb-2">{label}</p>
      <p className="text-xs text-gray-500 mb-2">Max. 100MB</p>
      <div className="grid grid-cols-4 gap-2">
        {languages.map((lang) => {
          const progress = uploadProgress?.[`${fieldId}-${lang}`] || 0;
          return (
            <div key={lang} className="border-2 border-gray-300 rounded p-3">
              <p className="text-xs font-bold mb-2">{lang.toUpperCase()}</p>
              {value?.[lang] && (
                <VideoPreview
                  videoValue={value[lang]}
                  onRemove={() => onChange({ ...value, [lang]: "" })}
                />
              )}
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) onChange({ ...value, [lang]: file });
                }}
                className="text-xs w-full"
              />
              {value?.[lang]?.type && !progress && (
                <p className="text-xs text-orange-500 mt-1">Pending upload</p>
              )}
              {progress > 0 && progress < 1 && (
                <div className="bg-gray-200 h-2 rounded mt-2">
                  <div
                    className="bg-orange-500 h-full rounded transition-all"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Carousel Item Component
const CarouselItem = ({ item, isSelected, onSelect, onDelete, thumbnail }) => (
  <div
    className={`relative flex-shrink-0 w-44 h-64 rounded-lg bg-[#535353] cursor-pointer overflow-hidden border-2 transition-all ${
      isSelected
        ? "border-orange-500 ring-2 ring-orange-300"
        : "border-gray-300 hover:border-gray-400"
    }`}
    onClick={() => onSelect(item)}>
    {thumbnail && (
      <img src={thumbnail} alt={item.referenceTitle} className="w-full h-full object-cover" />
    )}
    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-base px-2 py-2 text-center truncate">
      {item.referenceTitle || "Untitled"}
    </div>
    {!isSelected && (
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <PencilIcon className="w-12 h-12 text-orange-500 bg-[#FFEDD1] rounded-lg p-3" />
      </div>
    )}
  </div>
);

// Helper function to upload all pending files in multilingual object
const uploadMultilingualFiles = async (
  obj,
  basePath,
  fieldId,
  setUploadProgress,
  languages = LANGUAGES
) => {
  if (!obj) return obj;
  const result = { ...obj };
  for (const lang of languages) {
    if (result[lang]?.type) {
      const progressKey = `${fieldId}-${lang}`;
      result[lang] = await uploadFileIfNeeded(result[lang], basePath, (progress) => {
        setUploadProgress((prev) => ({
          ...prev,
          [progressKey]: progress?.progress || 0
        }));
      });
      // Clear progress after upload
      setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
    }
  }
  return result;
};

// Example Video Editor Component
const ExampleVideoEditor = ({
  languages,
  data,
  setData,
  onSave,
  saving,
  setSaving,
  uploadProgress,
  setUploadProgress,
  deleted,
  handleRestore
}) => {
  const handleSaveWithUpload = async () => {
    try {
      setSaving(true);
      const updatedData = { ...data };

      // Upload all pending thumbnails
      updatedData.thumbnail = await uploadMultilingualFiles(
        updatedData.thumbnail,
        "videosExamples",
        "example-thumbnail",
        setUploadProgress,
        languages
      );

      // Upload all pending videos
      updatedData.video = await uploadMultilingualFiles(
        updatedData.video,
        "videosExamples",
        "example-video",
        setUploadProgress,
        languages
      );

      setData(updatedData);
      await onSave(updatedData);
    } catch (error) {
      toast.error("Upload failed");
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#FFF5E6] rounded-lg p-6 mt-4">
      {deleted && (
        <div className="flex justify-between items-center mb-4">
          <p className="text-red-500 text-sm">This item is deleted</p>
          <Button.AdminPanelButton
            type="button"
            loading={saving}
            bgColor="#22C55E" // green-500 for restore
            textColor="#FFFFFF"
            isSave={true}
            onClick={() => handleRestore(data._id)}>
            Restore <FaTrashRestore className="w-4 h-4" />
          </Button.AdminPanelButton>
        </div>
      )}

      <AssignedProfileTypes data={data} />

      <div className="mb-4">
        <Input
          label="Reference Title"
          className="w-full"
          inputClassName="rounded-md border border-gray-300 bg-white"
          value={data?.referenceTitle || ""}
          onChange={(e) => setData({ ...data, referenceTitle: e.target.value })}
        />
      </div>

      <MultilingualInput
        label="Title"
        value={data?.title}
        onChange={(val) => setData({ ...data, title: val })}
        languages={languages}
      />

      <MultilingualInput
        label="Description"
        value={data?.description}
        onChange={(val) => setData({ ...data, description: val })}
        languages={languages}
      />

      <MultilingualImageUploader
        label="Video Thumbnail"
        value={data?.thumbnail}
        onChange={(val) => setData({ ...data, thumbnail: val })}
        uploadProgress={uploadProgress}
        fieldId="example-thumbnail"
        languages={languages}
      />

      <MultilingualVideoUploader
        label="Upload Video"
        value={data?.video}
        onChange={(val) => setData({ ...data, video: val })}
        uploadProgress={uploadProgress}
        fieldId="example-video"
        languages={languages}
      />

      <div className="flex justify-end">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={saving}
          bgColor="#FC9E5A"
          textColor="#FFFFFF"
          onClick={handleSaveWithUpload}>
          Save
        </Button.AdminPanelButton>
      </div>
    </div>
  );
};

// Video Tips Editor Component
const VideoTipsEditor = ({
  languages,
  data,
  setData,
  onSave,
  saving,
  setSaving,
  uploadProgress,
  setUploadProgress,
  deleted,
  handleRestore
}) => {
  const addTip = () => {
    const newTip = { ...defaultTipItem, id: uuidv4() };
    setData({ ...data, tips: [...(data?.tips || []), newTip] });
  };

  const updateTip = (index, field, value) => {
    const updatedTips = [...(data?.tips || [])];
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      updatedTips[index] = {
        ...updatedTips[index],
        [parent]: { ...updatedTips[index]?.[parent], [child]: value }
      };
    } else {
      updatedTips[index] = { ...updatedTips[index], [field]: value };
    }
    setData({ ...data, tips: updatedTips });
  };

  const deleteTip = (index) => {
    const updatedTips = data?.tips?.filter((_, i) => i !== index) || [];
    setData({ ...data, tips: updatedTips });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(data?.tips || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setData({ ...data, tips: items });
  };

  const handleSaveWithUpload = async () => {
    try {
      setSaving(true);
      const updatedData = { ...data };

      // Upload all pending tip icons
      if (updatedData.tips?.length > 0) {
        const updatedTips = [];
        for (const [index, tip] of updatedData.tips.entries()) {
          const updatedTip = { ...tip };
          if (tip.icon?.type) {
            const progressKey = `tip-icon-${index}`;
            updatedTip.icon = await uploadFileIfNeeded(tip.icon, "videosTips", (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [progressKey]: progress?.progress || 0
              }));
            });
            setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
          }
          updatedTips.push(updatedTip);
        }
        updatedData.tips = updatedTips;
      }

      setData(updatedData);
      await onSave(updatedData);
    } catch (error) {
      toast.error("Upload failed");
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#FFF5E6] rounded-lg p-6 mt-4">
      {deleted && (
        <div className="flex justify-between items-center mb-4">
          <p className="text-red-500 text-sm">This item is deleted</p>
          <Button.AdminPanelButton
            type="button"
            loading={saving}
            bgColor="#22C55E" // green-500 for restore
            textColor="#FFFFFF"
            isSave={true}
            onClick={() => handleRestore(data._id)}>
            Restore <FaTrashRestore className="w-4 h-4" />
          </Button.AdminPanelButton>
        </div>
      )}

      <AssignedProfileTypes data={data} />

      <div className="mb-4">
        <Input
          label="Reference Title"
          className="w-full"
          inputClassName="rounded-md border border-gray-300 bg-white"
          value={data?.referenceTitle || ""}
          onChange={(e) => setData({ ...data, referenceTitle: e.target.value })}
        />
      </div>

      <MultilingualInput
        label="Title"
        value={data?.title}
        onChange={(val) => setData({ ...data, title: val })}
        languages={languages}
      />

      {/* Explainer Section */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <p className="font-bold mb-3">Explainer Section</p>
        <MultilingualInput
          label="Title"
          value={data?.explainerSection?.title}
          onChange={(val) =>
            setData({ ...data, explainerSection: { ...data?.explainerSection, title: val } })
          }
          languages={languages}
        />
        <MultilingualInput
          label="Description"
          value={data?.explainerSection?.description}
          onChange={(val) =>
            setData({ ...data, explainerSection: { ...data?.explainerSection, description: val } })
          }
          languages={languages}
        />
      </div>

      {/* Tips Array - Draggable */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <div className="flex justify-between items-center mb-3">
          <p className="font-bold">Tips</p>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tips-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {data?.tips?.map((tip, index) => (
                  <Draggable
                    key={tip.id || index}
                    draggableId={tip.id || `tip-${index}`}
                    index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-semibold text-gray-600">
                            Tip #{index + 1}
                          </span>
                          <button onClick={() => deleteTip(index)} className="text-red-500">
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {languages.map((lang) => (
                            <Input
                              key={`title-${lang}`}
                              className="w-full"
                              inputClassName="rounded-md border border-gray-300 bg-white text-sm"
                              labelClassName="text-xs"
                              label={`Title (${lang.toUpperCase()})`}
                              value={tip?.title?.[lang] || ""}
                              onChange={(e) => updateTip(index, `title.${lang}`, e.target.value)}
                            />
                          ))}
                        </div>

                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {languages.map((lang) => (
                            <Input
                              key={`desc-${lang}`}
                              className="w-full"
                              inputClassName="rounded-md border border-gray-300 bg-white text-sm"
                              labelClassName="text-xs"
                              label={`Description (${lang.toUpperCase()})`}
                              value={tip?.description?.[lang] || ""}
                              onChange={(e) =>
                                updateTip(index, `description.${lang}`, e.target.value)
                              }
                            />
                          ))}
                        </div>

                        <div className="flex items-end gap-2">
                          <ImageUploader
                            label="Icon"
                            image={tip?.icon || ""}
                            setImage={(image) => updateTip(index, "icon", image)}
                            uploadProgress={uploadProgress?.[`tip-icon-${index}`] || 0}
                          />
                          {tip?.icon?.type && !uploadProgress?.[`tip-icon-${index}`] && (
                            <p className="text-xs text-orange-500">Pending upload</p>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="flex justify-end">
          <button
            onClick={addTip}
            className="flex items-center gap-1 text-sm bg-orange-500 text-white px-3 py-1 rounded">
            <PlusIcon className="w-4 h-4" /> Add Tip
          </button>
        </div>
      </div>

      {/* Support Information */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <p className="font-bold mb-3">Support Information</p>
        <MultilingualInput
          label="Title"
          value={data?.supportInformation?.title}
          onChange={(val) =>
            setData({ ...data, supportInformation: { ...data?.supportInformation, title: val } })
          }
          languages={languages}
        />
        <MultilingualInput
          label="Description"
          value={data?.supportInformation?.description}
          onChange={(val) =>
            setData({
              ...data,
              supportInformation: { ...data?.supportInformation, description: val }
            })
          }
          languages={languages}
        />
      </div>

      {/* Contact */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <p className="font-bold mb-3">Contact</p>
        <MultilingualInput
          label="Title"
          value={data?.contact?.title}
          onChange={(val) => setData({ ...data, contact: { ...data?.contact, title: val } })}
          languages={languages}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Name"
            className="w-full"
            inputClassName="rounded-md border border-gray-300 bg-white"
            value={data?.contact?.name || ""}
            onChange={(e) =>
              setData({ ...data, contact: { ...data?.contact, name: e.target.value } })
            }
          />
          <Input
            label="Email"
            className="w-full"
            inputClassName="rounded-md border border-gray-300 bg-white"
            value={data?.contact?.email || ""}
            onChange={(e) =>
              setData({ ...data, contact: { ...data?.contact, email: e.target.value } })
            }
          />
          <Input
            label="Phone"
            className="w-full"
            inputClassName="rounded-md border border-gray-300 bg-white"
            value={data?.contact?.phone || ""}
            onChange={(e) =>
              setData({ ...data, contact: { ...data?.contact, phone: e.target.value } })
            }
          />
          <Input
            label="WhatsApp"
            className="w-full"
            inputClassName="rounded-md border border-gray-300 bg-white"
            value={data?.contact?.whatsapp || ""}
            onChange={(e) =>
              setData({ ...data, contact: { ...data?.contact, whatsapp: e.target.value } })
            }
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={saving}
          bgColor="#FC9E5A"
          textColor="#FFFFFF"
          onClick={handleSaveWithUpload}>
          Save
        </Button.AdminPanelButton>
      </div>
    </div>
  );
};

// Video Page Editor Component
const VideoPageEditor = ({
  languages,
  data,
  setData,
  onSave,
  saving,
  setSaving,
  uploadProgress,
  setUploadProgress,
  deleted,
  handleRestore
}) => {
  const selectionTypeOptions = [
    { value: "", label: "None" },
    { value: "strength", label: "Strength" },
    { value: "role", label: "Role" }
  ];

  const handleSaveWithUpload = async () => {
    try {
      setSaving(true);
      const updatedData = { ...data };

      // Upload all pending thumbnails
      updatedData.thumbnail = await uploadMultilingualFiles(
        updatedData.thumbnail,
        "videosPages",
        "page-thumbnail",
        setUploadProgress,
        languages
      );

      // Upload all pending videos
      updatedData.video = await uploadMultilingualFiles(
        updatedData.video,
        "videosPages",
        "page-video",
        setUploadProgress,
        languages
      );

      setData(updatedData);
      await onSave(updatedData);
    } catch (error) {
      toast.error("Upload failed");
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#FFF5E6] rounded-lg p-6 mt-4">
      {deleted && (
        <div className="flex justify-between items-center mb-4">
          <p className="text-red-500 text-sm">This item is deleted</p>

          <Button.AdminPanelButton
            type="button"
            loading={saving}
            bgColor="#22C55E" // green-500 for restore
            textColor="#FFFFFF"
            isSave={true}
            onClick={() => handleRestore(data._id)}>
            Restore <FaTrashRestore className="w-4 h-4" />
          </Button.AdminPanelButton>
        </div>
      )}

      <AssignedProfileTypes data={data} />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Input
          label="Reference Title"
          className="w-full"
          inputClassName="rounded-md border border-gray-300 bg-white"
          value={data?.referenceTitle || ""}
          onChange={(e) => setData({ ...data, referenceTitle: e.target.value })}
        />
        <Input
          label="Index"
          type="number"
          className="w-full"
          inputClassName="rounded-md border border-gray-300 bg-white"
          value={data?.index || 1}
          // onChange={(e) => setData({ ...data, index: parseInt(e.target.value) || 1 })}
          disabled={true}
        />
      </div>

      <MultilingualInput
        label="Profile Page Title"
        value={data?.profilePageTitle}
        onChange={(val) => setData({ ...data, profilePageTitle: val })}
        languages={languages}
      />

      <MultilingualInput
        label="Profile Page Description"
        value={data?.profilePageDescription}
        onChange={(val) => setData({ ...data, profilePageDescription: val })}
        languages={languages}
      />

      <MultilingualInput
        label="Title"
        value={data?.title}
        onChange={(val) => setData({ ...data, title: val })}
        languages={languages}
      />

      <MultilingualInput
        label="Description"
        value={data?.description}
        onChange={(val) => setData({ ...data, description: val })}
        languages={languages}
      />

      <MultilingualInput
        label="Public Page Title"
        value={data?.publicPageTitle}
        onChange={(val) => setData({ ...data, publicPageTitle: val })}
        languages={languages}
      />

      <MultilingualImageUploader
        label="Thumbnail"
        value={data?.thumbnail}
        onChange={(val) => setData({ ...data, thumbnail: val })}
        uploadProgress={uploadProgress}
        fieldId="page-thumbnail"
        languages={languages}
      />

      {/* Tips Section */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <p className="font-bold mb-3">Tips Section</p>
        <MultilingualInput
          label="Tips Title"
          value={data?.tipsTitle}
          onChange={(val) => setData({ ...data, tipsTitle: val })}
          languages={languages}
        />
        <MultilingualInput
          label="Tips Description"
          value={data?.tipsDescription}
          onChange={(val) => setData({ ...data, tipsDescription: val })}
          languages={languages}
        />
        <MultilingualInput
          label="Tip Button Text"
          value={data?.tipButtonText}
          onChange={(val) => setData({ ...data, tipButtonText: val })}
          languages={languages}
        />
      </div>

      {/* Strength or Role Selection */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white">
        <p className="font-bold mb-3">Strength or Role Selection</p>
        <div className="mb-4">
          <InputSelect
            label="Selection Type"
            className="w-full"
            inputClassName="rounded-md border border-gray-300 bg-white"
            options={selectionTypeOptions}
            value={selectionTypeOptions.find(
              (opt) => opt.value === data?.strengthOrRoleSelection?.selectionType
            )}
            onChange={(val) =>
              setData({
                ...data,
                strengthOrRoleSelection: {
                  ...data?.strengthOrRoleSelection,
                  selectionType: val.value || undefined
                }
              })
            }
          />
        </div>
        <MultilingualInput
          label="Description"
          value={data?.strengthOrRoleSelection?.description}
          onChange={(val) =>
            setData({
              ...data,
              strengthOrRoleSelection: { ...data?.strengthOrRoleSelection, description: val }
            })
          }
          languages={languages}
        />
      </div>

      <div className="flex justify-end">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={saving}
          bgColor="#FC9E5A"
          textColor="#FFFFFF"
          onClick={handleSaveWithUpload}>
          Save
        </Button.AdminPanelButton>
      </div>
    </div>
  );
};

const AssignedProfileTypes = ({ data }) => {
  if (!data?.profileTypes || !data?.profileTypes?.length || !Array.isArray(data?.profileTypes))
    return null;

  return (
    <div className="flex w-full flex-col gap-2 mb-4">
      <p className="text-lg font-medium">This video is assigned to following profile types</p>
      <div className="flex flex-row flex-wrap gap-4 w-full">
        {data?.profileTypes?.map((profileType) => {
          return (
            <span className="px-4 py-1 bg-orange-500 text-white rounded-md text-base">
              {profileType?.name}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default function ProfileVideos() {
  const { orgId } = useParams();
  const [profile] = useContext(ProfileContext);
  const { organizations } = profile;

  const [org, setOrg] = useState(null);
  const [activeTab, setActiveTab] = useState("examples");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [examples, setExamples] = useState([]);
  const [tips, setTips] = useState([]);
  const [pages, setPages] = useState([]);

  // Selected item for editing
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState(null);

  // Upload progress tracking with unique IDs
  const [uploadProgress, setUploadProgress] = useState({});

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newReferenceTitle, setNewReferenceTitle] = useState("");

  useEffect(() => {
    if (!organizations) return;

    const organization = organizations.find((org) => org._id === orgId);
    if (organization) {
      setOrg(organization);
    }
  }, [organizations, orgId]);

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      if (activeTab === "examples") {
        const { data } = await getVideosExamples(orgId);
        setExamples(data || []);
      } else if (activeTab === "tips") {
        const { data } = await getVideosTips(orgId);
        setTips(data || []);
      } else if (activeTab === "pages") {
        const { data } = await getVideosPages(orgId);
        setPages(data || []);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [orgId, activeTab]);

  useEffect(() => {
    fetchData();
    setSelectedItem(null);
    setEditData(null);
  }, [activeTab, org]);

  // Get current items based on active tab
  const getCurrentItems = () => {
    switch (activeTab) {
      case "examples":
        return examples;
      case "tips":
        return tips;
      case "pages":
        return pages;
      default:
        return [];
    }
  };

  // Get thumbnail for carousel item
  const getThumbnail = (item) => {
    if (activeTab === "examples" || activeTab === "pages") {
      const thumb =
        item?.thumbnail?.en || item?.thumbnail?.de || item?.thumbnail?.fr || item?.thumbnail?.it;
      if (thumb && typeof thumb === "string") {
        return process.env.REACT_APP_AWS_URL + thumb;
      }
      return null;
    }
    return null;
  };

  // Handle item selection
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setEditData({ ...item });
    setUploadProgress({}); // Clear upload progress when switching items
  };

  // Handle create new item
  const handleCreate = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!newReferenceTitle.trim()) {
      toast.error("Please enter a reference title");
      return;
    }

    setSaving(true);
    try {
      let newItem;
      if (activeTab === "examples") {
        newItem = { ...defaultExample, referenceTitle: newReferenceTitle };
        const newExample = await createVideosExample(newItem, orgId);
        handleSelectItem(newExample.data);
      } else if (activeTab === "tips") {
        newItem = { ...defaultTip, referenceTitle: newReferenceTitle };
        const newTip = await createVideosTip(newItem, orgId);
        handleSelectItem(newTip.data);
      } else if (activeTab === "pages") {
        newItem = { ...defaultPage, referenceTitle: newReferenceTitle };
        const newPage = await createVideosPage(newItem, orgId);
        handleSelectItem(newPage.data);
      }

      toast.success("Created successfully");
      setShowCreateModal(false);
      setNewReferenceTitle("");
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  // Handle save (accepts data from editor with uploaded files)
  const handleSave = async (dataToSave) => {
    const data = dataToSave || editData;
    if (!data?._id) return;

    try {
      if (activeTab === "examples") {
        await updateVideosExample(orgId, data._id, data);
      } else if (activeTab === "tips") {
        await updateVideosTip(orgId, data._id, data);
      } else if (activeTab === "pages") {
        await updateVideosPage(orgId, data._id, data);
      }

      toast.success("Saved successfully");
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;

    setSaving(true);
    try {
      if (activeTab === "examples") {
        await deleteVideosExample(orgId, deleteTarget._id);
      } else if (activeTab === "tips") {
        await deleteVideosTip(orgId, deleteTarget._id);
      } else if (activeTab === "pages") {
        await deleteVideosPage(orgId, deleteTarget._id);
      }

      toast.success("Deleted successfully");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      if (selectedItem?._id === deleteTarget._id) {
        setSelectedItem(null);
        setEditData(null);
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  // Handle restore
  const handleRestore = async (restoreTargetId) => {
    if (!restoreTargetId) return;

    setSaving(true);
    try {
      if (activeTab === "examples") {
        await restoreVideosExample(orgId, restoreTargetId);
      } else if (activeTab === "tips") {
        await restoreVideosTip(orgId, restoreTargetId);
      } else if (activeTab === "pages") {
        await restoreVideosPage(orgId, restoreTargetId);
      }

      toast.success("Restored successfully");
      if (selectedItem?._id === restoreTargetId) {
        setSelectedItem(null);
        setEditData(null);
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to restore");
    } finally {
      setSaving(false);
    }
  };

  // Render editor based on active tab
  const renderEditor = () => {
    if (!editData) return null;

    switch (activeTab) {
      case "examples":
        return (
          <ExampleVideoEditor
            languages={org?.languages || LANGUAGES}
            data={editData}
            setData={setEditData}
            onSave={handleSave}
            saving={saving}
            setSaving={setSaving}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            deleted={editData?.deleted}
            handleRestore={handleRestore}
          />
        );
      case "tips":
        return (
          <VideoTipsEditor
            languages={org?.languages || LANGUAGES}
            data={editData}
            setData={setEditData}
            onSave={handleSave}
            saving={saving}
            setSaving={setSaving}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            deleted={editData?.deleted}
            handleRestore={handleRestore}
          />
        );
      case "pages":
        return (
          <VideoPageEditor
            languages={org?.languages || LANGUAGES}
            data={editData}
            setData={setEditData}
            onSave={handleSave}
            saving={saving}
            setSaving={setSaving}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            deleted={editData?.deleted}
            handleRestore={handleRestore}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <PageWrapper pageHeading="Profile Videos Setup">
        {/* Tab Selector */}
        <div className="flex gap-2 mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-orange-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Carousel Section */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">
              Created {TABS.find((t) => t.id === activeTab)?.label}:
            </h3>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-all">
              <PlusIcon className="w-5 h-5" />
              Add new {activeTab === "examples" ? "video" : activeTab === "tips" ? "tip" : "page"}
            </button>
          </div>

          {/* Carousel */}
          <div className="flex gap-3 overflow-x-auto py-4">
            {getCurrentItems().length === 0 && !loading && (
              <p className="text-gray-500 py-4">
                No items yet. Click the button above to create one.
              </p>
            )}
            {getCurrentItems().map((item) => (
              <div
                key={item._id}
                className="relative group"
                style={{
                  filter: item.deleted ? "grayscale(100%)" : "none"
                }}>
                <CarouselItem
                  item={item}
                  isSelected={selectedItem?._id === item._id}
                  onSelect={handleSelectItem}
                  thumbnail={getThumbnail(item)}
                />
                {!item.deleted && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(item);
                      setShowDeleteModal(true);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Editor Section */}
        {renderEditor()}

        {/* Create Modal */}
        <BaseModal open={showCreateModal} setOpen={setShowCreateModal} title="Create New Item">
          <form onSubmit={handleCreate}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Title *
            </label>
            <input
              name="referenceTitle"
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={newReferenceTitle}
              onChange={(e) => setNewReferenceTitle(e.target.value)}
              placeholder="Enter reference title"
            />
            <div className="mt-4 flex justify-end">
              <Button.AdminPanelButton
                type="button"
                isSave={true}
                loading={saving}
                bgColor="#FC9E5A"
                textColor="#FFFFFF"
                onClick={handleCreate}>
                Create
              </Button.AdminPanelButton>
            </div>
          </form>
        </BaseModal>

        {/* Delete Confirmation Modal */}
        <BaseModal open={showDeleteModal} setOpen={setShowDeleteModal} title="Confirm Delete">
          <div>
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete "{deleteTarget?.referenceTitle}"?
            </p>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <Button.AdminPanelButton
                type="button"
                isSave={true}
                loading={saving}
                bgColor="#EF4444"
                textColor="#FFFFFF"
                onClick={handleDelete}>
                Delete
              </Button.AdminPanelButton>
            </div>
          </div>
        </BaseModal>

        {loading && <FullScreenLoader text="Loading..." />}
      </PageWrapper>
    </div>
  );
}
